import { useRef, useState } from 'react';
import { ImagePlus, Loader2, Trash2 } from 'lucide-react';
import supabase, { waitForSession } from '../lib/supabase';

export type PhotoUploaderProps = {
  /** Storage bucket id. */
  bucket: 'club-photos' | 'court-photos' | 'coach-photos';
  /**
   * Path prefix under the bucket. Must begin with the club_id segment
   * so Storage RLS (manages_club) passes:
   *   club-photos/<club_id>
   *   court-photos/<club_id>/<court_id>
   */
  pathPrefix: string;
  /** Current list of photo URLs (will be edited in place via onChange). */
  photos: string[];
  /** Called after an upload or delete with the new full URL list. */
  onChange: (next: string[]) => void;
  /** Upper bound, default 8. */
  max?: number;
  /** Compact layout for inline use in tables etc. */
  compact?: boolean;
};

/**
 * Multi-image uploader that writes to Supabase Storage and maintains a
 * string[] of public URLs. Drop into any form and pass {photos, onChange}.
 * Callers are responsible for persisting the URL list to their own table.
 */
export default function PhotoUploader({
  bucket, pathPrefix, photos, onChange, max = 8, compact = false,
}: PhotoUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const prefix = pathPrefix.replace(/^\/+|\/+$/g, '');

  const onFilesPicked = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setErr(null);
    setUploading(true);
    try {
      await waitForSession();

      const nextUrls: string[] = [...photos];
      for (const file of Array.from(files)) {
        if (nextUrls.length >= max) break;
        const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
        const stamp = Date.now().toString(36);
        const rand = Math.random().toString(36).slice(2, 8);
        const path = `${prefix}/${stamp}-${rand}.${ext}`;

        // Race the upload against a 30s timeout. supabase-js doesn't expose
        // an AbortSignal on upload(); the underlying request keeps flying
        // but the UI escapes instead of spinning forever when the response
        // never lands (e.g. flaky HTTP/3 leg where the POST succeeded
        // server-side but the reply packet got dropped).
        let result: { error: { message: string } | null };
        try {
          result = await Promise.race([
            supabase.storage.from(bucket).upload(path, file, {
              cacheControl: '3600', upsert: false, contentType: file.type,
            }),
            new Promise<never>((_, reject) =>
              setTimeout(
                () => reject(new Error(
                  'Upload took longer than 30s. The file may already be saved — reload and check before retrying.',
                )),
                30_000,
              ),
            ),
          ]) as { error: { message: string } | null };
        } catch (e: any) {
          setErr(e?.message ?? 'Upload failed');
          break;
        }
        if (result.error) {
          setErr(result.error.message);
          break;
        }
        const { data } = supabase.storage.from(bucket).getPublicUrl(path);
        nextUrls.push(data.publicUrl);
      }
      onChange(nextUrls);
    } catch (e: any) {
      setErr(e?.message ?? 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = async (url: string) => {
    // Extract the object path from the public URL (everything after "/<bucket>/")
    const marker = `/storage/v1/object/public/${bucket}/`;
    const idx = url.indexOf(marker);
    const objectPath = idx >= 0 ? url.slice(idx + marker.length) : null;

    if (objectPath) {
      // Best-effort delete -- if it fails (e.g. RLS), still remove from the list
      // so a stale URL doesn't stick around in the UI.
      try {
        await waitForSession();
        await supabase.storage.from(bucket).remove([objectPath]);
      } catch {
        // Ignore storage delete failures and still update the UI list.
      }
    }
    onChange(photos.filter((u) => u !== url));
  };

  const canAddMore = photos.length < max;
  const tileClass = compact
    ? 'w-16 h-16 rounded-lg'
    : 'w-28 h-28 rounded-xl';

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {photos.map((url) => (
          <div
            key={url}
            className={`relative overflow-hidden border border-gray-200 bg-gray-50 ${tileClass}`}
          >
            <img src={url} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => remove(url)}
              className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center"
              aria-label="Remove photo"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}

        {canAddMore && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className={`${tileClass} border-2 border-dashed border-gray-300 hover:border-[#2d5016] hover:bg-[#c6f806]/10 text-gray-500 flex flex-col items-center justify-center gap-1 text-xs disabled:opacity-60`}
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Uploading
              </>
            ) : (
              <>
                <ImagePlus className="w-5 h-5" />
                Add photo
              </>
            )}
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/avif"
        multiple
        className="hidden"
        onChange={(e) => onFilesPicked(e.target.files)}
      />

      <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
        <span>
          {photos.length}/{max} · JPEG / PNG / WebP up to 10&nbsp;MB
        </span>
      </div>

      {err && (
        <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
          {err}
        </div>
      )}
    </div>
  );
}
